/**
 * ChartJsonBlock — LLM이 ```charts``` 블록으로 응답한 JSON 스펙을 Recharts로 렌더링
 * - 차트 이미지 다운로드 버튼 포함
 */

import { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const getPrimaryColor = () =>
  getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#6366f1';

const FALLBACK_COLORS = ['#4F8EF7', '#F7A14F', '#6BCB77', '#FF6B6B', '#A78BFA', '#F472B6', '#34D399', '#FBBF24'];

const getColors = () => {
  const primary = getPrimaryColor();
  return [primary, ...FALLBACK_COLORS.filter(c => c.toLowerCase() !== primary.toLowerCase())];
};

export interface ChartSpec {
  type: 'bar' | 'line' | 'pie' | 'doughnut' | string;
  title?: string;
  labels?: string[];
  datasets: {
    label?: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    yAxisID?: string;
    type?: string;
  }[];
}

interface Props { specs: ChartSpec[]; }

function SingleChart({ spec, containerRef }: { spec: ChartSpec; containerRef: React.RefObject<HTMLDivElement> }) {
  const COLORS = getColors();
  const labels = spec.labels || [];
  const type = spec.type || 'bar';

  const data = labels.map((label, i) => {
    const entry: Record<string, unknown> = { name: label };
    spec.datasets.forEach(ds => { entry[ds.label || '값'] = ds.data[i] ?? 0; });
    return entry;
  });
  const seriesKeys = spec.datasets.map(ds => ds.label || '값');

  if (type === 'pie' || type === 'doughnut') {
    const ds = spec.datasets[0];
    const pieData = labels.map((label, i) => ({ name: label, value: ds?.data[i] ?? 0 }));
    return (
      <div ref={containerRef}>
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
              innerRadius={type === 'doughnut' ? 60 : 0} outerRadius={100} label>
              {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip cursor={false} /><Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (type === 'line') {
    return (
      <div ref={containerRef}>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip cursor={false} /><Legend />
            {seriesKeys.map((k, i) => (
              <Line key={k} type="monotone" dataKey={k}
                stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip cursor={false} /><Legend />
          {seriesKeys.map((k, i) => (
            <Bar key={k} dataKey={k} fill={COLORS[i % COLORS.length]} radius={[3, 3, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ChartJsonBlock({ specs }: Props) {
  const [activeIdx, setActiveIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  if (!specs || specs.length === 0) return null;
  const spec = specs[activeIdx] || specs[0];

  const handleDownload = async () => {
    if (!containerRef.current) return;
    try {
      const canvas = await html2canvas(containerRef.current, { backgroundColor: '#ffffff', scale: 2 });
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `chart_${Date.now()}.png`;
      a.click();
    } catch (e) {
      console.error('차트 다운로드 오류:', e);
    }
  };

  return (
    <div className="chat-chart-block">
      {spec.title && <div className="chat-chart-title">{spec.title}</div>}
      {specs.length > 1 && (
        <div className="chat-chart-tabs">
          {specs.map((s, i) => (
            <button key={i}
              className={`chat-chart-tab${activeIdx === i ? ' active' : ''}`}
              onClick={() => setActiveIdx(i)}>
              {s.title || `차트 ${i + 1}`}
            </button>
          ))}
        </div>
      )}
      <SingleChart spec={spec} containerRef={containerRef} />
      <div className="chat-chart-actions">
        <button className="chat-chart-dl-btn" onClick={handleDownload} title="차트 이미지 다운로드">
          다운로드
        </button>
      </div>
    </div>
  );
}
